import { getConfig } from "../config/globalConfig";
import { AppConfig } from "../config/loadConfigJson";

import { OgcParams } from "../models/geo/ogcParams";

import { authService } from "../services/auth.service";
import { ApiError } from "../services/utils/apiError";


export class BaseWebService {
    public appConfig: AppConfig;

    constructor() {
        const appConfig = getConfig();

        if (!appConfig) {
            throw new Error('Configuration not available');
        }

        this.appConfig = appConfig;
    }

    protected async makeRequestSimple<T>(
        endpoint: string,
        method: 'GET' | 'POST' | 'PUT' | 'DELETE',
        headers: HeadersInit,
        body?: any
    ): Promise<T> {

        // const fileResponse = await fetch(`/data/mock/organisaties/get_list_sharing.json`, {
        //     method: 'GET',
        // });

        // if (!fileResponse.ok) {
        //     throw new Error(`HTTP error! status: ${fileResponse.status}`);
        // }
    
        // return await fileResponse.json();

        try {
            const response = await fetch(endpoint, {
                method,
                headers,
                body: body ? JSON.stringify(body) : undefined,
            });
    
            if (!response.ok) {
                await this.handleErrors(response);
            }
    
            if (response.status === 204) {
                return { ok: true } as unknown as T;
            }
    
            return await response.json();
        } 
        catch (error) {
            console.error(`Error during request [${method}] to ${endpoint}:`, error);
            throw error;
        }
    }

    protected async makeRequest<T>(
        endpoint: string,
        method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
        headers: HeadersInit = {},
        body?: any,
        retries: number = 0,
        wrapResponse: boolean = false,
        bubbleUpErrors: boolean = false
    ): Promise<T> {
        let attempt = 0;
    
        while (true) {
            try {
                const response = await fetch(endpoint, {
                    method,
                    headers,
                    body: (body instanceof FormData)
                        ? body
                        : (body ? JSON.stringify(body) : undefined),
                });
    
                if (!response.ok) {
                    if (bubbleUpErrors) {
                        // Return de volledige response zodat het component zelf beslist
                        // const errorJson = await response.json();

                        const text = await response.text();
                        let errorMessage: string;
                        try {
                            const json = JSON.parse(text);
                            errorMessage = json.message || JSON.stringify(json);
                        } 
                        catch {
                            errorMessage = text;
                        }

                        return {
                            error: true,
                            status: response.status,
                            message: errorMessage // typeof errorJson === 'string' ? errorJson : errorJson.message || 'Onbekende fout'
                        } as unknown as T;
                    } 
                    else {
                        await this.handleErrors(response);
                    }
                }

                // Handle 204 and 201 with no content
                const status = response.status;
                if (status === 204 || status === 201) {
                    const text = await response.text();
                    if (!text.trim()) {
                        return wrapResponse
                            ? { ok: true } as unknown as T
                            : undefined as unknown as T;
                    }
    
                    const json = JSON.parse(text);
                    return wrapResponse
                        ? { ok: true, ...json } as unknown as T
                        : json;
                }
    
                 // All other responses
                const text = await response.text();
                const result = text.trim() ? JSON.parse(text) : undefined;

                return wrapResponse
                    ? { ok: true, ...(result ?? {}) } as unknown as T
                    : result as T;

            } 
            catch (error) {
                attempt++;
    
                if (attempt > retries) {
                    console.error(`Request failed after ${attempt} attempts to ${endpoint}:`, error);
                    throw error;
                }
    
                console.warn(`Retrying (${attempt}/${retries}) for ${endpoint}...`);
                await this.wait(200 * attempt);
            }
        }
    }
    
    private wait(ms: number) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    // // Method to convert ListParams to query string
    // protected createQueryParams(params: any): string {
    //     const queryParams = new URLSearchParams();

    //     // Loop through the object keys and append them to the query string
    //     Object.keys(params).forEach((key) => {
    //     const value = params[key];
    //     if (value !== null && value !== undefined) {
    //         queryParams.append(key, value.toString());
    //     }
    //     });

    //     return queryParams.toString();
    // }

    /* Params */

    protected createOgcParams(ogcParams: OgcParams | null): string {
        let params = '';
        if (ogcParams !== null) {
            params = this.appendParam(params, 'bbox', ogcParams.bbox);
            params = this.appendParam(params, 'srsName', ogcParams.srsName);
            params = this.appendParam(params, 'offset', ogcParams.offset);
            params = this.appendParam(params, 'limit', ogcParams.limit);

            if (ogcParams.searchText) {
                params = this.appendParam(params, 'searchText', `${ogcParams.searchText}*`); // Wildcard voor searchText
            }

            if (ogcParams.properties) {
                params = this.appendParam(params, 'properties', ogcParams.properties);
            }

            if (ogcParams.filter) {
                params = this.appendParam(params, 'filter', ogcParams.filter);
            }

            return params;
        }
        return '';
    }

    protected createListParams(listParams: any | null): string {
        let params = '';
        if (listParams !== null) {
            // Standard params (legacy API style)
            params = this.appendParam(params, 'state', listParams.state);
            params = this.appendParam(params, 'offset', listParams.offset);
            params = this.appendParam(params, 'limit', listParams.limit);
            params = this.appendParam(params, 'sortBy', listParams.sortBy);
            params = this.appendParam(params, 'filter', listParams.filter);
            params = this.appendParam(params, 'id', listParams.id);
            params = this.appendParam(params, 'directSharedOnly', listParams.directSharedOnly);
            
            if (listParams.searchText) {
                params = this.appendParam(params, 'searchText', `${listParams.searchText}*`);
            }

            // Underscore-prefixed params (new API style)
            params = this.appendParam(params, '_offset', listParams._offset);
            params = this.appendParam(params, '_limit', listParams._limit);
            params = this.appendParam(params, '_sort', listParams._sort);

            if (listParams._search) {
                params = this.appendParam(params, '_search', `${listParams._search}`);
            }

            return params;
        }
        return '';
    }

    public appendParam(params: string, param: string, paramValue: any, addIfEmpty: boolean = false): string {
        const isParamValueValid = paramValue !== undefined && paramValue !== null && paramValue !== '';
        const isParamsEmpty = !params;
    
        if (isParamValueValid) {
            const trimmedValue = paramValue.toString().trim();
            params += (isParamsEmpty ? '?' : '&') + param + '=' + encodeURIComponent(trimmedValue).replace(/'/g, '%27');
        }
        else if (addIfEmpty) {
            params += (isParamsEmpty ? '?' : '&') + param + '=';
        }
    
        return params;
    }

    /* HttpHeaders */

    // response type 'arraybuffer'
    protected async getHeadersDownload(useAuthorization: boolean = true, organizationId?: string) {
        return await this.getHeaders('', useAuthorization, organizationId);
    }
    
    // response type 'blob'
    protected async getHeadersPdf(useAuthorization: boolean = true, organizationId?: string) {
        return await this.getHeaders('application/pdf', useAuthorization, organizationId);
    }
    
    // response type 'blob'
    protected async getHeadersZip(useAuthorization: boolean = true, organizationId?: string) {
        return await this.getHeaders('application/zip', useAuthorization, organizationId);
    }
    
    // response type 'json'
    protected async getHeadersJson(useAuthorization: boolean = true, organizationId?: string) {
        return await this.getHeaders('application/json', useAuthorization, organizationId);
    }

    protected async getHeadersUploadGeopackage(useAuthorization: boolean = true, organizationId?: string) {
        return await this.getHeaders('application/json', useAuthorization, organizationId);
    }
    
    // Refactor met de layer feature branch daar is dit ook al gedaan
    protected async getHeadersJsonForOrganization(organizationId: string, useAuthorization: boolean = true) {
        const headers = await this.getHeadersJson(useAuthorization);
        return {
            ...headers,
            'x-Organization-ID': organizationId,
        };
    }

    private async getHeaders(contentType: string, useAuthorization: boolean = true, organizationId?: string): Promise<HeadersInit> {
        let headers: Record<string, string> = {
            'Accept': 'application/json, text/json',
        };
        
        if (contentType) {
            headers['Content-Type'] = contentType;
        }
        
        if (useAuthorization) {
            const token = await authService.getAccessToken();
        
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            } 
            else {
                console.error('Geen token beschikbaar');
            }
        }
        
        if (organizationId) {
            headers['x-Organization-ID'] = organizationId;
        }
        
        return headers;
    }

    protected async handleErrors(response: Response): Promise<never> {
        let body: any = null;
      
        try {
          const text = await response.text();
          body = text ? JSON.parse(text) : null;
        }
        catch {
          body = null;
        }
      
        const message =
          body?.message ||
          body?.error?.message ||
          response.statusText ||
          "Server Error";
      
        if (response.status === 404) {
            console.warn(`API ${response.status} bij ${response.url} → ${message}`);
        }
        else {
            console.error(`❌ API ${response.status} bij ${response.url} → ${message}`);
        }
      
        throw new ApiError(response.status, message, body, response.url);
    }
}
  